import { GroupType, ITreeItemResponse } from '@kcftech/prometheus-client';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useTreeItem } from 'custom_hooks/data_hooks/tree/useTreeItem';
import { buildTreeChildrenData } from 'pages/workbench/side_panel/workbench_tree/modals/shared';
import { useTreeItemDataLoader } from 'pages/workbench/side_panel/workbench_tree/modals/useTreeItemDataLoader';
import {
    TreeItemGroupLabel,
    TreeItemGroupLabelText,
} from 'pages/workbench/side_panel/workbench_tree/styled';
import { TreeItemGroupIcon } from 'pages/workbench/side_panel/workbench_tree/TreeItemGroupIcon';
import { isNodeInHierarchy } from 'pages/workbench/side_panel/workbench_tree/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import { createTree } from 'shared/data_tree/tree';
import { TreeExpansionConfig, TreeSelectionConfig } from 'shared/data_tree/types';
import { excludeNullAndUndefined, removeAllItems, toggleArrayItem } from 'utils/array';

export type ModelAggregateGroupsInputProps = Readonly<{
    treeId: string;
    rootGroupId: string;
    expandedGroupIds: string[];
    onExpandedChange: (groupIds: string[]) => void;
    selectedGroupIds: string[];
    onSelectedChange: (groupIds: string[]) => void;
    isCollectorDependentSystem?: boolean;
}>;

export function ModelAggregateGroupsInput({
    treeId,
    isCollectorDependentSystem,
    rootGroupId,
    onExpandedChange,
    expandedGroupIds,
    selectedGroupIds,
    onSelectedChange,
}: ModelAggregateGroupsInputProps) {
    const { showToast } = useNotificationContext();
    const { loadingIds, loadChildren } = useTreeItemDataLoader();
    const [tree, setTree] = useState(() => createTree<ITreeItemResponse>());

    const onExpandedChangeRef = useRef(onExpandedChange);
    onExpandedChangeRef.current = onExpandedChange;

    const expandedGroupIdsRef = useRef(expandedGroupIds);
    expandedGroupIdsRef.current = expandedGroupIds;

    const onSelectedChangeRef = useRef(onSelectedChange);
    onSelectedChangeRef.current = onSelectedChange;

    const { data: rootTreeItem, isLoading: isLoadingRootTreeItem } =
        useTreeItem(rootGroupId);

    const expandable = useMemo<TreeExpansionConfig<ITreeItemResponse>>(
        () => ({
            isExpanded: ({ data: treeItem }) => expandedGroupIds.includes(treeItem.Id),
            canExpand: ({ data: treeItem }) =>
                treeItem.HasChildren &&
                treeItem.TreeItemType !== GroupType.MonitoringPoint,
            onChange: ({ node: { data: treeItem } }) =>
                onExpandedChangeRef.current(
                    toggleArrayItem(expandedGroupIds, treeItem.Id),
                ),
            isLoading: ({ data: treeItem }) => loadingIds.includes(treeItem.Id),
            onLoadChildren: ({ data: treeItem }) => {
                loadChildren({
                    parentIds: [treeItem.Id],
                    onSuccess: (parentId, children) => {
                        setTree((current) =>
                            current.replaceChildren({
                                id: parentId,
                                childData: buildTreeChildrenData(children),
                            }),
                        );
                    },
                    onError: (parentId) => {
                        showToast({ type: 'error', content: 'Unable to load children.' });
                        onExpandedChangeRef.current(
                            removeAllItems(expandedGroupIdsRef.current, [parentId]),
                        );
                    },
                });
            },
            indentNonExpandableRoots: false,
        }),
        [expandedGroupIds, loadChildren, loadingIds, showToast],
    );

    const selectable = useMemo<TreeSelectionConfig<ITreeItemResponse>>(
        () => ({
            selectionStyle: 'checkbox',
            isSelected: ({ data: treeItem }) => selectedGroupIds.includes(treeItem.Id),
            onChange: ({ node, isSelected }) => {
                if (isSelected) {
                    const filteredGroupIds = excludeNullAndUndefined(
                        selectedGroupIds.map((id) =>
                            tree.has(id) ? tree.get(id) : undefined,
                        ),
                    )
                        .filter(
                            (selectedNode) =>
                                !isNodeInHierarchy(selectedNode, node.hierarchy) &&
                                !isNodeInHierarchy(node, selectedNode.hierarchy),
                        )
                        .map((selectedNode) => selectedNode.data.Id);

                    onSelectedChangeRef.current([...filteredGroupIds, node.data.Id]);
                } else {
                    onSelectedChangeRef.current(
                        removeAllItems(selectedGroupIds, [node.data.Id]),
                    );
                }
            },
        }),
        [selectedGroupIds, tree],
    );

    useEffect(() => {
        if (rootTreeItem && !tree.hasRoot(rootTreeItem.Id)) {
            setTree(() => createTree([{ id: rootTreeItem.Id, data: rootTreeItem }]));
        }
    }, [rootTreeItem, tree]);

    useEffect(() => {
        const parentIds = tree.allNodes
            .filter(
                (node) =>
                    expandable.isExpanded(node) &&
                    !node.children &&
                    !loadingIds.includes(node.data.Id),
            )
            .map((node) => node.data.Id);

        if (parentIds.length > 0) {
            loadChildren({
                parentIds,
                onSuccess: (parentId, children) => {
                    setTree((current) =>
                        current.replaceChildren({
                            id: parentId,
                            childData: buildTreeChildrenData(children),
                        }),
                    );
                },
                onError: (parentId) => {
                    showToast({ type: 'error', content: 'Unable to load children.' });
                    onExpandedChangeRef.current(
                        removeAllItems(expandedGroupIdsRef.current, [parentId]),
                    );
                },
            });
        }
    }, [expandable, loadChildren, loadingIds, showToast, tree.allNodes]);

    return (
        <SpinnerArea isLoading={isLoadingRootTreeItem}>
            <DataTree<ITreeItemResponse>
                treeId={treeId}
                data={tree}
                renderNode={({
                    node: { isRoot, data: treeItem },
                    isExpanded,
                    isSelectable,
                }) => (
                    <TreeItemGroupLabel $isDisabled={!isSelectable}>
                        <TreeItemGroupIcon
                            isCollectorDependentSystem={isCollectorDependentSystem}
                            treeItemType={treeItem.TreeItemType}
                            isExpanded={isExpanded}
                        />
                        <TreeItemGroupLabelText $isRoot={isRoot}>
                            {treeItem.Name}
                        </TreeItemGroupLabelText>
                    </TreeItemGroupLabel>
                )}
                expandable={expandable}
                selectable={selectable}
            />
        </SpinnerArea>
    );
}
